<template>
	<div v-if="!pageLoadAborted" class="mb-2xl">
		<v-container class="mb-xl md:grid md:auto-rows-auto md:grid-cols-12">
			<!-- Breadcrumbs-->
			<div
				class="col-span-5 col-start-8 row-start-1 shrink-0 px-sm pt-sm md:px-0 md:pt-lg"
			>
				<v-breadcrumbs type="product" :product="activeProduct" />
			</div>

			<!--
            Badges & product images
            Row 1, col 1 - 7
            -->

			<div
				class="relative col-span-6 col-start-1 row-span-full mt-lg pb-sm md:flex md:pb-0"
			>
				<div class="md:sticky md:bottom-0 md:flex-1 md:self-end">
					<v-page-product-gallery
						class="md:sticky md:top-24 md:min-h-[calc(100vh-1.5rem)]"
						:active-product="activeProduct"
						:medias="medias"
						:price="activeProduct.pricing.price"
						:promotion-price="activeProduct.pricing.promotionPrice"
						:original-price="
							currentLang === 'et'
								? activeProduct.pricing.originalPrice
								: activeProduct.pricing.lowestPrice
						"
						:promotion="primaryPromotion"
						:attributes="listingAttributes"
						:product-badges="productBadges"
					/>
				</div>
			</div>

			<!--
                Product details
                Name, ident, price, discounted price, part payment
                Row 1, col 8 - 12
            -->
			<div
				class="col-span-5 col-start-8 row-[2/-1] space-y-lg px-sm pt-md pb-lg md:mt-xl md:px-0"
			>
				<div class="flex flex-col items-start justify-between gap-y-sm">
					<v-h1 tag="h1" no-style lang="fi">
						<span class="flex items-center justify-between space-x-md">
							<v-h4
								class="m-0 block max-w-full wrap-break-word hyphens-auto uppercase"
								tag="span"
								lang="fi"
							>
								{{ activeProduct.title || activeProduct.name }} &nbsp;
							</v-h4>
						</span>

						<span
							v-if="activeProduct.specification"
							class="m-0 block pt-2 text-base leading-4 tracking-xl normal-case"
						>
							{{ activeProduct.specification }}
						</span>
					</v-h1>

					<v-page-product-rating-stars-small
						:skus="skus"
						@trigger="isRatingOpen = true"
					/>
				</div>

				<v-page-product-price
					:active-product="activeProduct"
					:product-selection-incomplete="configValidation.$error"
					:active-product-pending="false"
					:dimensions="dimensions"
					:promotion-info="promotionInfo"
					:promotion-info-pending="!productDataIsFresh"
					:lowest-price="activeProduct.pricing.lowestPrice.amount"
					:hide-price-history="promotionInfo.hidePriceHistory"
					:omnibus-content="promotionInfo.omnibusContent"
				/>

				<div class="space-y-2">
					<v-page-product-disclaimer
						v-for="disclaimer in activeProduct.disclaimers.slice(0, 2)"
						:key="disclaimer.id"
						:disclaimer="disclaimer"
					/>
				</div>

				<!-- Product configurator -->
				<v-page-product-configurator
					v-if="productConfiguration"
					v-model:selected-product="variantProduct"
					v-model:selected-properties="selectedProperties"
					:configuration="productConfiguration"
					:product-properties="initialProduct.properties"
					:configurable-product="parentProduct"
					:show-live-configurator="isSofa"
					:mask-medias="parentProduct?.maskMedias ?? []"
				/>

				<!-- Add to cart/wishlist-->
				<div class="mb-lg flex flex-row space-x-xs">
					<!-- Again, passing :disabled="!productDataIsFresh"
                    didn't work, so don't do anything about that! WTF nuxt -->
					<v-button
						v-if="canAddToCart"
						:theme="checkoutButtonTheme"
						class="flex-1"
						tabindex="0"
						:aria-label="t('button.add-to-cart')"
						@click="addToCart"
					>
						{{ t("button.add-to-cart") }}
						<span
							v-if="
								productDetails?.cart.multiplier &&
								productDetails?.cart.multiplier > 1
							"
						>
							{{ productDetails?.cart.multiplier }} {{ t("button.quantity") }}
						</span>
					</v-button>

					<v-button
						v-else-if="
							activeProduct.inventory.state === ProductInventoryState.OutOfStock
						"
						theme="gray"
						class="flex-1 cursor-not-allowed"
						disabled
					>
						{{ t("button.only-from-outlet") }}
					</v-button>

					<v-button
						v-else-if="
							activeProduct.inventory.state ===
							ProductInventoryState.AvailableInOutlet
						"
						theme="gray"
						class="flex-1 cursor-not-allowed"
						disabled
					>
						{{ t("button.only-from-outlet") }}
					</v-button>

					<v-button
						v-else-if="configValidation.$errors.length > 0"
						theme="gray"
						class="flex-1 cursor-not-allowed"
						:aria-label="t('button.add-to-cart-disabled')"
						disabled
					>
						{{ t("button.add-to-cart-disabled") }}
					</v-button>

					<v-button
						v-else
						theme="gray"
						class="flex-1 cursor-not-allowed"
						:aria-label="t('button.add-to-cart-disabled')"
						disabled
					>
						{{ t("button.only-from-outlet") }}
					</v-button>

					<v-button
						theme="gray"
						:aria-label="t('wishlist')"
						@click="addToWishlist"
					>
						<v-icon icon="heart" class="h-md"></v-icon>
					</v-button>
				</div>

				<!-- Most of our usual product info isn't relevant for gift
                cards or service products (no stock availability etc) -->
				<div v-if="isServiceProduct" class="space-y-lg">
					<!-- eslint-disable vue/no-v-html -->
					<div
						v-if="shortDescription"
						class="prose"
						v-html="shortDescription"
					></div>

					<client-only>
						<v-page-product-details-group
							v-if="fullDescription"
							:title="`${t('product-description')}`"
						>
							<!-- eslint-disable vue/no-v-html -->
							<div class="prose" v-html="fullDescription"></div>
						</v-page-product-details-group>
					</client-only>
				</div>

				<!-- Regular stockable products -->
				<div v-else class="space-y-lg">
					<div
						class="flex flex-col divide-y divide-gray-100 rounded-2xs border border-gray-100"
					>
						<!--
                            1. Delivery time estimate
                            2. Shipping price
                            3. Outlet availability
                        -->
						<v-page-product-availability
							:product="activeProduct"
							:product-details="productDetails"
							:promotion-info="promotionInfo"
						/>

						<v-page-product-shop-picker
							:product="activeProduct"
							:product-details="productDetails"
						/>
					</div>

					<!-- Short description of the product -->
					<!-- eslint-disable vue/no-v-html -->
					<div
						v-if="shortDescription"
						class="prose"
						v-html="shortDescription"
					></div>

					<!-- Product's additional information -->
					<div>
						<v-page-product-details
							:attribute-values="attributeValues"
							:description="fullDescription"
							:dimensions="dimensions"
							:parcels="parcels"
							:sku="sku"
							:assets="assets"
							:is-parcels-visible="isParcelsVisible"
							:qa-items="activeProduct.qaItems"
						/>

						<v-page-product-rating-stars
							:skus="skus"
							@click="isRatingOpen = true"
							@keypress="isRatingOpen = true"
						/>
					</div>
				</div>
			</div>
		</v-container>

		<!-- Recommendations, row 2 -->
		<v-page-product-alternative-recommendations-list
			v-if="
				alternativeRecommendations.before &&
				alternativeRecommendations.before.productGroups.length > 0 &&
				alternativeRecommendations.after &&
				alternativeRecommendations.after.productGroups.length > 0
			"
			:before="alternativeRecommendations.before"
			:after="alternativeRecommendations.after"
			class="mb-xl"
		/>

		<template v-for="list in otherRecommendationLists" :key="list.id">
			<v-page-product-recommendation-list
				v-if="list.productGroups.length > 0"
				:list="list"
				class="mb-xl"
			/>
		</template>

		<!-- Product info modals -->
		<v-page-product-rating-modal
			:is-open="isRatingOpen"
			:sku="activeProduct.sku || ''"
			:skus="skus"
			@close="isRatingOpen = false"
		></v-page-product-rating-modal>

		<client-only>
			<!-- Blog section, row 3 -->
			<div v-if="articleData.length > 0" class="mb-lg bg-gray-50">
				<!-- Related articles to product from blog -->
				<v-container class="py-lg">
					<v-scrollable
						class="mb-md flex snap-x snap-proximity flex-nowrap space-x-md px-sm sm:px-0 md:mb-0 md:grid md:grid-cols-3 md:gap-md md:space-x-0"
					>
						<v-page-product-article
							v-for="article in articleData"
							:key="article.uuid"
							:article="article"
						/>
					</v-scrollable>
				</v-container>
			</div>
		</client-only>
	</div>
</template>

<script setup lang="ts">
import {computedAsync, watchDebounced, whenever} from "@vueuse/core";
import {useGtm} from "@gtm-support/vue-gtm";
import {useVuelidate} from "@vuelidate/core";
import type {ProductPage} from "@apptus/esales-api";
import {useClientHandle} from "@urql/vue";
import {useQuery} from "@pkg/shared/composables/use-query";
import {print} from "graphql";
import {marked} from "marked";
import {formatISO} from "date-fns";
import sanitizeHtml from "sanitize-html";
import {uniqBy} from "es-toolkit";
import {useToast} from "@pkg/shared/store/toast";
import {getVoyadoKey} from "@pkg/shared/utils/product";
import {
	type JsonLD,
	type Thing,
	type ProductKind,
	type BlokAsset,
	type PromotionInfo,
	ProductMediaSubtype,
	type ProductMediaDataFragmentWithSubtype,
} from "@pkg/shared/types";
import {
	type SimpleProductDataFragment,
	type ConfigurableProductDataFragment,
	type ProductMediaDataFragment,
	ProductInventoryState,
	ProductState,
	type ViewProductQuery,
	type ViewProductQueryVariables,
	ViewProductDocument,
	type ViewProductCriticalQuery,
	ViewProductCriticalDocument,
	type ProductAttributeDataFragment,
	type Maybe,
} from "@pkg/shared/graphql";
import type {RecommendationListSettings} from "@pkg/shared/composables/use-elevate";

import {useCart} from "@pkg/shared/composables/use-cart";
import {useElevate} from "@pkg/shared/composables/use-elevate";
import {useGtmValues} from "@pkg/shared/composables/use-gtm-values";
import {useLifecycle} from "@pkg/shared/composables/use-lifecycle";
import {useReviews} from "@pkg/shared/composables/use-reviews";
import {useSeoUrl} from "@pkg/shared/composables/use-seo";
import {useStoryblokArticles} from "@pkg/shared/composables/use-storyblok-articles";
import {useStoryblokStoriesList} from "@pkg/shared/composables/use-storyblok-stories-list";
import {richTextToString} from "@pkg/shared/utils/rich-text-to-string";
import transformImageObject from "@pkg/shared/utils/transform-image-object";

type Product = ProductKind<ViewProductQuery["product"]>;

type DetailProduct = Extract<
	ViewProductQuery["product"],
	{__typename: "SimpleProduct"}
>;

type ProductAttributeValue = ProductAttributeDataFragment;
type ProductParcel = DetailProduct["parcels"][0];
type Promotion = DetailProduct["promotions"][0];
type ProductBadge = DetailProduct["productBadges"][0];

const isRatingOpen = ref(false);
const route = useRoute();
const {csrf} = useCsrf();

const gtmValues = useGtmValues();

const configValidation = useVuelidate();

const {client: urqlClientHandle} = useClientHandle();

const productSlug = route.params.product?.toString() ?? "";

const {t} = useI18n();
const {t: tg} = useI18n({useScope: "global"});
const gtm = useGtm();

const {isMounted} = useLifecycle();

const {$i18n} = useNuxtApp();
const currentLang = computed(() => $i18n.locale.value);

const checkoutButtonTheme = computed<"black" | "checkout">(() =>
	tg("elements.checkout-button") === "black" ? "black" : "checkout",
);

// Get info about the requested product, as well as its parent and siblings if it's part of a configurable product.
// Note: This is performed once for the whole product and its possible siblings, not when selecting configuration attributes for a configurable product (because the `productSlug` variable isn't reactive, this component re-initializes when the route slug changes). It's also awaited, we don't try to render anything before we have this data. Much of our product info on the page doesn't change during client-side usage - it's mostly just `variantProduct` and `activeProduct` that can dynamically change.
// However, we keep this and data derived from it reactive because we may want to fetch new fresh data to replace the initial value we have if we use caching for the product page + its hydration payload.
// Note: ViewProduct includes the details fields (attributes, parcels, etc.) for the initial active product, eliminating the need for a second sequential fetch on initial load. When the user selects a different variant, a new ViewProduct fetch is made in the variantProduct watcher.
const {data: initialProductRes} = await useQuery<
	ViewProductQuery,
	ViewProductQueryVariables
>({
	query: ViewProductDocument,
	variables: {slug: productSlug},
});

// Extract the actual product value from the query above.
const initialProductMaybe = computed<Product | undefined>(() =>
	initialProductRes.value?.product.__typename === "SimpleProduct" ||
	initialProductRes.value?.product.__typename === "ConfigurableProduct"
		? initialProductRes.value.product
		: undefined,
);

if (!initialProductMaybe.value) {
	throw createError({
		statusCode: 404,
		statusMessage: "Page Not Found",
		fatal: true,
	});
}

if (initialProductMaybe.value.state === ProductState.Draft) {
	throw createError({
		statusCode: 410,
		statusMessage: "Gone",
		fatal: true,
		data: {product: initialProductMaybe.value},
	});
}

// See above
const initialProduct = computed(() => initialProductMaybe.value as Product);

// No need to be reactive
const initialCanonicalSlug = initialProduct.value.seo.slug;

// Use this to instruct our code not to perform heavy operations if we're about to navigate away from the current page
const pageLoadAborted = ref(false);

// If users arrive by an old URL (slug) to our product page, we tell their browser explicitly about the updated product, better for SEO than having multiple different URLs responding with code 200 for the same product.
// Note that this happens only in the initial SSR render. This means it's efficient (redirection is done on the server-side without extra HTTP round-trips, just the one necessary one that causes this page to be re-triggered with the new product URL), and HTTP codes aren't relevant during client-side navigation anyway. In the client-side we also have a watcher that updates the browser's URL to contain the newest slug in case the initial one was outdated.
if (
	initialCanonicalSlug &&
	initialCanonicalSlug !== productSlug &&
	import.meta.server
) {
	// Note that this doesn't stop script execution. We couldn't find a good way to stop it, only throwing an error would be able to do that but then we couldn't get nuxt not to show an error page.
	await navigateTo(`/${tg("storyblok.product")}/${initialCanonicalSlug}`, {
		redirectCode: 301,
	});

	pageLoadAborted.value = true;
}

// The related configurable product, if any relevant (not set if the page is displaying a completely simple product).
const parentProduct = computed<ConfigurableProductDataFragment | undefined>(
	() =>
		initialProduct.value.__typename === "ConfigurableProduct"
			? initialProduct.value
			: (initialProduct.value.parentProduct ?? undefined),
);

// If we have a related configurable product, this is its default simple variant product.
const defaultChildProduct = computed<SimpleProductDataFragment | undefined>(
	() => parentProduct.value?.configuration.defaultProduct ?? undefined,
);

// Used by configurator
const productConfiguration = computed(() =>
	initialProduct.value.__typename === "ConfigurableProduct"
		? initialProduct.value.configuration
		: initialProduct.value.parentProduct?.configuration,
);

// Extract the details for the initial active product from the merged ViewProduct query result.
// For a SimpleProduct page the details are directly on the product; for a ConfigurableProduct page they are on configuration.defaultProduct.
const initialProductDetails = computed<DetailProduct | undefined>(() => {
	const product = initialProductRes.value?.product;
	if (product?.__typename === "SimpleProduct") {
		return product;
	}
	if (product?.__typename === "ConfigurableProduct") {
		const defaultProduct = product.configuration.defaultProduct;
		if (defaultProduct) {
			return defaultProduct;
		}
	}
	return undefined;
});

// Below, `variantProduct` and `activeProduct` are writable refs and they don't automatically change if the base product data gets refreshed

// See below
const getInitialVariantProductValue = () =>
	initialProduct.value.__typename === "ConfigurableProduct"
		? defaultChildProduct.value
		: initialProduct.value.parentProduct
			? initialProduct.value
			: undefined;

// This is initially set to the default simple product (that also then gets selected and is editable by the page's product configurator), IF we're dealing with a configurable product. If we're dealing with a simple product, this won't be set.
const variantProduct = ref<SimpleProductDataFragment | undefined>(
	getInitialVariantProductValue(),
);

const initialActiveProduct = initialProductDetails.value;

if (!initialActiveProduct) {
	throw createError({
		statusCode: 404,
		statusMessage: "Page Not Found",
		fatal: true,
	});
}

// This is the actual selected product, either just the initial fetched product itself if this is a simple product without any related configurable products, or the current selected variant of a parent configurable product. This is updated below whenever the product configurator makes changes to the selected variant (`variantProduct`).
const activeProduct = ref<DetailProduct>(initialActiveProduct);

// Whenever the variant selection is changed through the product configurator, fetch the full variant
// data (both core and detail fields) and update activeProduct. The configurator only emits { id, sku, seo { slug } },
// so we use ViewProduct (by slug) to get all data in one request.
// Skip if the emitted id matches the current product (happens on initial configurator setup).
whenever(variantProduct, async (value) => {
	if (!value) return;
	if (value.id === activeProduct.value.id) return;
	if (!value.seo.slug) return;

	const res = await urqlClientHandle.query<
		ViewProductQuery,
		ViewProductQueryVariables
	>(ViewProductDocument, {slug: value.seo.slug});

	if (res.data?.product?.__typename === "SimpleProduct") {
		activeProduct.value = res.data.product;
	}
});

const selectedProperties = ref<Record<string, string>>({});

// Update browser URL if variant selection changes, and also currently right when navigating to page too
whenever(
	activeProduct,
	(value, oldValue) => {
		if (import.meta.server) {
			return;
		}

		// If the slug of the product is somehow not set, avoid updating the URL at all since that would only make things worse. In practice this doesn't happen since all our published products should have a slug (because they should have a valid title etc).
		if (!value.seo.slug) {
			return;
		}

		if (oldValue && oldValue.seo.slug === value.seo.slug) {
			return;
		}

		const url = new URL(window.location.href);

		url.pathname = `/${tg("storyblok.product")}/${value.seo.slug}`;

		// Circumvent nuxt routing to prevent the page from scrolling to the top by vue-router when the URL path changes. Nuxt still thinks we're on the initial variant's page if we do it this way. NOTE that this has at least the side effect that if you select a variant, the URL gets changed, and then you navigate away from the page and back using your browser's back button, nuxt will return you to the initial variant's page rather than the last selected variant / configuration since it isn't aware of the URL changes that happened. Shouldn't cause any other problems.
		window.history.replaceState(null, "", url.pathname + url.search);
		// NOTE: We could have used nuxt's `router.options` `scrollBehavior` config, which configures the underlying vue-router (see the link below). However, `scrollBehavior` overwrites vue-router's whole default scrolling behavior globally, which means it's hard to make scroll retaining work exactly like it works by default (I think the browser manages it by default) - for example, when we used the solution suggested by stackoverflow, pressing the "Load more" button on product listings caused the page to scroll to the top, which didn't happen natively. So that option seems too risky.
		// https://stackoverflow.com/questions/73049943/scroll-behaviour-not-working-in-nuxt3-application/74447828#74447828
		// router.replace(url.pathname + url.search);
	},
	{immediate: true},
);

// For the initial product, details come from initialProductDetails (already fetched by ViewProduct).
// For a selected variant, activeProduct was set from a full ViewProductDocument fetch so it contains all detail fields too.
const productDetails = computed<DetailProduct | undefined>(() => {
	if (activeProduct.value.id === initialActiveProduct.id) {
		return initialProductDetails.value;
	}
	return activeProduct.value;
});

// We're now using nuxt's SWR for the product page (see nuxt.config). So initially nuxt is likely to return a cached HTML page to the client, containing potentially outdated data about the product. Nuxt runs this setup script during hydration to ensure the page data is up-to-date - we set this variable in the CSR phase to let our UI components know that we're now playing with for sure up-to-date product data.
const productDataIsFresh = ref(false);

if (import.meta.client) {
	productDataIsFresh.value = true;
}
onBeforeMount(() => {
	productDataIsFresh.value = true;
});

// You can use this if caching is done in Cloudflare and we want to make nuxt fetch the freshest data after the initial cached HTML has been rendered. This isn't needed when using nuxt SWR since nuxt already does something like this on its own.
// if (import.meta.client) {
//     await Promise.all([
//         refreshBaseData().then(() => {
//             const candidateActiveProduct = getInitialActiveProductValue();
//             if (candidateActiveProduct) {
//                 activeProduct.value = candidateActiveProduct;
//                 variantProduct.value = getInitialVariantProductValue();
//
//                 baseDataIsFresh.value = true;
//             }
//         }),
//         refreshDetailsData().then(() => {
//             detailsDataIsFresh.value = true;
//         }),
//     ]);
// }

// After the page mounts, re-fetch only the most time-sensitive fields (price, stock) to ensure
// they reflect the latest state even when the page HTML came from a Nuxt or Cloudflare SWR cache.
onMounted(async () => {
	// Perform the request fully outside of URQL so that this fresh product data doesn't touch its graph cache, since that would trigger urql to re-fetch everything on this page relating to the product, nullifying our attempts to performantly fetch just the main strategic data.
	// We use the initialActiveProduct on purpose as this is supposed be fired only on the initial mount (variant changes will trigger another fetch)
	const res = await $fetch<{data: ViewProductCriticalQuery}>("/graphql", {
		method: "POST",
		body: {
			query: print(ViewProductCriticalDocument),
			variables: {id: initialActiveProduct.id},
		},

		headers: csrf ? {"csrf-token": csrf} : {},
	});

	if (res.data?.product?.__typename === "SimpleProduct") {
		activeProduct.value = {
			...activeProduct.value,
			...res.data.product,
		};
	}
});

const isServiceProduct = computed(
	() =>
		activeProduct.value.inventory.isGiftCard ||
		activeProduct?.value?.productClass?.title === "PALVELU",
);
const attributeValues = computed<ProductAttributeValue[]>(
	() => productDetails.value?.attributes || [],
);
const listingAttributes = computed<ProductAttributeValue[]>(
	() => productDetails.value?.listingAttributes || [],
);
const parcels = computed<ProductParcel[]>(
	() => productDetails.value?.parcels || [],
);
const isParcelsVisible = computed(
	() => productDetails?.value?.parcelsAreVisible,
);
const dimensions = computed<DetailProduct["dimensions"] | undefined>(
	() => productDetails.value?.dimensions,
);
const categoryNames = computed(
	() =>
		productDetails.value?.categories.map((category) =>
			category.title.toLowerCase(),
		) || [],
);

const categoryIds = computed(
	() => productDetails.value?.categories.map((category) => category.id) || [],
);

const isSofa = computed(
	() =>
		productDetails.value?.categories.some(
			(x) => x.pimCode === "sofas_and_armchairs",
		) ?? false,
);

const {data: benefitStories} = useStoryblokStoriesList(
	"product-benefits-v2",
	{
		starts_with: "global/product-benefits-v2",
	},
	{lazy: true},
);

// Benefits that aren't linked to a category are global
const globalBenefitStories = computed(() =>
	(benefitStories.value ?? []).filter(
		(benefit) => !benefit.content?.category?.source?.id,
	),
);
// Benefit stories that match any of the product's categories
const categoryBenefitStories = computed(() =>
	(benefitStories.value ?? []).filter((benefit) =>
		categoryIds.value.some((categId) =>
			benefit.content?.category?.source?.id.includes(categId),
		),
	),
);

const globalBenefitImages = computed(() =>
	globalBenefitStories.value.flatMap(
		(benefit) =>
			benefit.content?.images.map((image: BlokAsset) =>
				transformImageObject(image),
			) || [],
	),
);
const categoryBenefitImages = computed(() =>
	categoryBenefitStories.value.flatMap(
		(benefit) =>
			benefit.content?.images.map((image: BlokAsset) =>
				transformImageObject(image),
			) || [],
	),
);

// Move withType to outer scope

const withSubtype = (
	items: ProductMediaDataFragment[],
	subtype: ProductMediaSubtype = ProductMediaSubtype.other,
) => items.map((item) => ({...item, subtype}));

const medias = computed<ProductMediaDataFragmentWithSubtype[]>(() => {
	const activeProductMedias =
		activeProduct.value.medias || defaultChildProduct.value?.medias || [];
	const parentProductMedias = parentProduct.value?.medias || [];

	return uniqBy(
		[
			...withSubtype(activeProductMedias),
			// If the product itself has medias, bump the position of parent images to be after them
			...(activeProductMedias.length > 0
				? parentProductMedias.map((item) => ({
						...item,
						isDefault: false,
						position: item.position + 100,
						subtype: ProductMediaSubtype.other,
					}))
				: withSubtype(parentProductMedias)),
			...withSubtype(
				categoryBenefitImages.value,
				ProductMediaSubtype.benefitImage,
			),
			...withSubtype(
				globalBenefitImages.value,
				ProductMediaSubtype.benefitImage,
			),
		],
		(item) => item.id,
	); // Assuming 'id' is the unique key for media items
});

const productBadges = computed<ProductBadge[]>(
	() => activeProduct.value.productBadges.slice(0, 8) || [],
);

// Fetch articles from StoryBlok
const {data: articlesResponse} = useStoryblokArticles(
	{
		perPage: 3,
		extraFilters: computed(() => ({
			rooms_article_category: {in_array: categoryNames.value?.join(",") ?? ""},
		})),
	},
	{lazy: true},
);
const articleData = computed(() => articlesResponse.value?.stories || []);

const cart = useCart();
const wishlist = useCart("wishlist");
const toast = useToast();
const {$eventbus} = useNuxtApp();
const {getProductPage} = useElevate();

const gaProduct = computed(
	() =>
		activeProduct.value && {
			...gtmValues.productToGa(activeProduct.value, 1),
			...Object.fromEntries(
				categoryNames.value
					?.slice(0, 5)
					.map((category, index) => [
						`item_category${index > 0 ? index + 1 : ""}`,
						category,
					]) || [],
			),
		},
);

/** Purchace logic */
async function addToCart() {
	if (!activeProduct.value.id || !gaProduct.value) {
		return;
	}

	try {
		const cartResponse = await cart.addProduct(activeProduct.value.id);
		const cartResponseData = cartResponse.data?.cartProductCreate;
		const cartResponseOk =
			cartResponseData?.__typename === "CartProductCreatePayload";

		const cartData = cartResponseOk ? cartResponseData.cart : undefined;

		let gaCartProducts: ReturnType<typeof gtmValues.productToGa>[] = [];
		if (cartData) {
			gaCartProducts = cartData.products.map((cartProduct) =>
				gtmValues.productToGa(cartProduct.product, cartProduct.quantity),
			);
		}

		gtm?.trackEvent({
			event: "add_to_cart",
			_clear: true,
			ecommerce: {
				currency: "EUR",
				value: gaProduct.value.price,
				items: [gaProduct.value],
				...gtmValues.cart.value,
			},
		});

		setTimeout(() => {
			gtm?.trackEvent({
				event: "va_add_to_cart",
				_clear: true,
				ecommerce: {
					currency: "EUR",
					value: cartData ? Number(cartData.price.amount) : 0,
					items: gaCartProducts,
					...gtmValues.cart.value,
				},
			});
		}, 1000);

		// Provide the 'added quantity' as a readonly value to render correct amount in 'added to cart toast'
		const cartMultiplier = productDetails?.value?.cart.multiplier?.valueOf();
		$eventbus.emit("product-added-to-cart", {
			productId: activeProduct.value.id,
			quantity: cartMultiplier,
		});
	} catch {
		toast.add("warning", t("toast.cart-warning"));
	}
}

const canAddToCart = computed(
	() =>
		configValidation.value.$errors.length === 0 &&
		activeProduct.value.inventory.isAvailableForPurchase,
);

/** Wishlist logic */
async function addToWishlist() {
	if (!activeProduct.value.id || !gaProduct.value) {
		return;
	}

	gtm?.trackEvent({
		event: "add_to_wishlist",
		_clear: true,
		ecommerce: {
			currency: "EUR",
			value: gaProduct.value.price,
			items: [gaProduct.value],
		},
	});

	try {
		await wishlist.addProduct(activeProduct.value.id);

		$eventbus.emit("product-added-to-wishlist", {
			productId: activeProduct.value.id,
		});
	} catch {
		toast.add("warning", t("toast.wishlist-warning"));
	}
}

const recommendationLists = ref<ProductPage["recommendationLists"]>([]);

const alternativeRecommendations = computed<{
	before?: ProductPage["recommendationLists"][0];
	after?: ProductPage["recommendationLists"][0];
}>(() => ({
	before: recommendationLists.value.find(
		(list) => list.id === "alternatives-before",
	),
	after: recommendationLists.value.find(
		(list) => list.id === "alternatives-after",
	),
}));

const otherRecommendationLists = computed<ProductPage["recommendationLists"]>(
	() =>
		recommendationLists.value.filter(
			(list) => !list.id.startsWith("alternatives"),
		),
);

const gtmEventSent = ref(false); // Flag to track if the GTM event has been sent

watchDebounced(
	[activeProduct, productDetails],
	([value, details]) => {
		if (!value || !details || !gaProduct.value || gtmEventSent.value) {
			return;
		}

		gtm?.trackEvent({
			event: "view_item",
			_clear: true,
			ecommerce: {
				currency: "EUR",
				value: gaProduct.value.price,
				items: [gaProduct.value],
			},
		});
		gtmEventSent.value = true;
	},
	{
		debounce: 500,
		immediate: true,
	},
);

watch(
	[activeProduct, isMounted],
	async ([activeProductValue, isMountedValue]) => {
		// Since recommendations aren't awaited to be in the first render, we only start trying to fetch them after mount so that we won't hit SSR-CSR rendering mismatches (hydration errors).
		if (!(isMountedValue && activeProductValue)) {
			return;
		}

		const groups: RecommendationListSettings[] = [
			{
				id: "alternatives-before",
				algorithm: "ALTERNATIVES",
				limit: 2,
				productRules: `rule incl price [0, ${activeProduct.value.pricing.price?.amount ?? "Infinity"}]`,
			},
			{
				id: "alternatives-after",
				algorithm: "ALTERNATIVES",
				limit: 18,
				productRules: `rule incl price [${activeProduct.value.pricing.price?.amount ?? "infinity"}, Infinity]`,
			},
			{
				id: "more-from-series",
				algorithm: "MORE_FROM_SERIES",
			},
			{
				id: "style-with",
				algorithm: "STYLE_WITH",
			},
			{
				id: "upsell",
				algorithm: "UPSELL",
			},
			{
				id: "recently-viewed",
				algorithm: "RECENTLY_VIEWED",
			},
		];

		try {
			const response = await getProductPage(
				getVoyadoKey(
					"product",
					// If the active product is visible, it will have its data in Voyado at the `product` level. If it's hidden, it's a variant of a parent product (at the `variant` level in Voyado) and its parent will be at the `product` level.
					activeProductValue.state === ProductState.Published ||
						!activeProductValue.parentProduct
						? activeProductValue.id
						: activeProductValue.parentProduct.id,
				),
				groups.map((settings) => ({
					limit: 20,
					visualization: "GRID",
					label: t(
						`product-recommendations.${settings.id.replace(/-(before|after)$/, "")}`,
					),
					...settings,
				})),
			);

			recommendationLists.value = response.recommendationLists;
		} catch {
			//
		}
	},
	{immediate: true},
);

interface PriorityGroup {
	priority: number;
	promotions: Promotion[];
}

const priorityGroups = computed<PriorityGroup[]>(() =>
	(productDetails.value?.promotions || [])
		.reduce<PriorityGroup[]>((accumulator, promotion) => {
			const priority = promotion.priority || 0;
			const group = accumulator.find((item) => item.priority === priority);

			if (group) {
				group.promotions.push(promotion);
			} else {
				accumulator.push({
					priority,
					promotions: [promotion],
				});
			}
			return accumulator;
		}, [])
		.sort((a, b) => b.priority - a.priority),
);

const primaryPromotion = computed<Promotion | undefined>(
	() => priorityGroups.value[0]?.promotions[0],
);

const promotionInfo = computed<PromotionInfo>(() => {
	const info: PromotionInfo = {
		activeTo: undefined,
		hidePriceHistory:
			primaryPromotion.value?.style?.productPage.hidePriceHistory || false,
		showEndingTimer:
			primaryPromotion.value?.style?.productPage.showPromotionEndingTimer ||
			false,
		showCountdownTextContent:
			primaryPromotion.value?.style?.productPage.showCountdownTextContent ||
			false,
		promotionTimerContent:
			primaryPromotion.value?.style?.productPage.promotionTimerContent || "",
		deliveryPricingInfoOverride:
			primaryPromotion.value?.style?.productPage.deliveryPricingInfoOverride ||
			"",
		omnibusContent:
			primaryPromotion.value?.style?.productPage.omnibusContent || "",
		isTimedVisibility:
			!!primaryPromotion.value?.style?.productPage.isTimedVisibility,
		isCampaignModalEnabled:
			!!primaryPromotion.value?.style?.productPage.isCampaignModalEnabled,
		campaignModalContent:
			primaryPromotion.value?.style?.productPage.campaignModalContent || "",
		style: primaryPromotion.value?.style,
	};

	if (info.showEndingTimer && primaryPromotion.value?.activeTo) {
		info.activeTo = new Date(primaryPromotion.value.activeTo);
	} else {
		for (const group of priorityGroups.value) {
			for (const promotion of group.promotions) {
				if (promotion.activeTo && new Date(promotion.activeTo) > new Date()) {
					info.activeTo = new Date(promotion.activeTo);
				}
			}
		}
	}

	return info;
});

const sku = computed(() => activeProduct.value.sku || undefined);

const skus = computed(() => {
	if (parentProduct.value) {
		return parentProduct.value.configuration.products
			.map((item) => item.sku)
			.filter((item): item is string => !!item);
	}

	if (activeProduct.value.sku) {
		return [activeProduct.value.sku];
	}

	return [];
});

const shortDescription = computedAsync<string | undefined>(async () => {
	if (activeProduct.value.shortDescription) {
		return await marked.parse(activeProduct.value.shortDescription);
	}
	if (parentProduct.value?.shortDescription) {
		return await marked.parse(parentProduct.value.shortDescription);
	}
	return undefined;
});

const fullDescription = computedAsync<string | undefined>(async () => {
	if (activeProduct.value.description) {
		return await marked.parse(activeProduct.value.description);
	}
	if (parentProduct.value?.description) {
		return await marked.parse(parentProduct.value.description);
	}
	return undefined;
});

const assets = computed(() => {
	if (
		parentProduct.value !== undefined &&
		parentProduct.value.assets.length > 0
	) {
		return parentProduct.value.assets;
	}

	return activeProduct.value.assets || [];
});

// Note that this doesn't include club promotion pricing. Those require login so we're not allowed to promote them.
const currentCanonicalPrice = computed(() =>
	Number(activeProduct.value.pricing.price?.amount),
);

definePageMeta({
	alias: ["/tuote/:product", "/toote/:product", "/toode/:product"],
});

/** Schema structure */
const seo = useSeoUrl();
const seoUrl = ref("");

watch(
	() => activeProduct.value,
	async (value) => {
		if (value) {
			seoUrl.value = seo.useUrl(value?.seo?.slug || "");
		}
	},
	{immediate: true},
);

// Sanitize the description for SEO meta tags.
const descriptionsForSanitization = ref(
	activeProduct.value.shortDescription || parentProduct.value?.shortDescription,
);

// Sanitize the description for SEO meta tags.
const sanitizedDescription = computed(() =>
	sanitizeHtml(descriptionsForSanitization.value ?? "", {
		allowedTags: [],
	})
		.replaceAll("\n", " ")
		.slice(0, 200),
);

// This switch statement is used to determine the availability of the product for Schema.org.
const getJsonldAvailability = (schemaProduct: SimpleProductDataFragment) => {
	switch (schemaProduct.inventory.state) {
		case ProductInventoryState.InStock: {
			return schemaProduct.inventory.deliveryEstimateDays !== null &&
				schemaProduct.inventory.deliveryEstimateDays <= 7
				? "https://schema.org/InStock"
				: "https://schema.org/BackOrder";
		}
		case ProductInventoryState.AvailableInOutlet: {
			return "https://schema.org/InStoreOnly";
		}
		case ProductInventoryState.AvailableToOrder: {
			return "https://schema.org/BackOrder";
		}
		default: {
			return "https://schema.org/OutOfStock";
		}
	}
};

const {getRating, getSchemaReviews} = useReviews();

// These are used for the JSON-LD schema.
const {rating, totalRatingCount} = getRating(skus.value);
// Note that there's no await here - otherwise we'd risk the reviews.io API delaying the rendering of our entire product page (unless we use some super strict timeouts in the reviews API call). We hopefully usually get this info from cache, in which case it should come in quick enough.
const {schemaReviews} = getSchemaReviews(skus.value);

// Maximum delivery estimate is the delivery estimate days + 5 days.
// This is only used for the JSON-LD schema.
const maximumDeliveryEstimate = computed(() => {
	const {deliveryEstimateDays} = activeProduct.value.inventory;
	return deliveryEstimateDays == null ? 0 : deliveryEstimateDays + 5;
});

// Minimum delivery estimate is the delivery estimate days - 2 days.
// This is only used for the JSON-LD schema.
const minimumDeliveryEstimate = computed(() => {
	const {deliveryEstimateDays} = activeProduct.value.inventory;
	return deliveryEstimateDays == null ? 0 : deliveryEstimateDays - 2;
});

// We assume dimensions are configured for the variant (simple products)
const maybeWithUnit = (
	value: Maybe<number> | undefined,
	unitText: "cm" | "kg",
) =>
	value
		? {
				"@type": "QuantitativeValue" as const,
				value,
				unitText,
			}
		: undefined;
const dimensionsForLd = {
	width: maybeWithUnit(activeProduct?.value?.dimensions.width, "cm"),
	height: maybeWithUnit(activeProduct?.value?.dimensions.height, "cm"),
	depth: maybeWithUnit(activeProduct?.value?.dimensions.depth, "cm"),
	weight: maybeWithUnit(activeProduct?.value?.dimensions.weight, "kg"),
};

const colorsForLd = (
	activeProduct?.value?.colorForFilteringAttribute.find(
		(a) => a.__typename === "ProductMultiselectValue",
	)?.multiselect || []
)
	.map((ms) => ms.title)
	.filter((v) => !!v)
	.join(",");

const materialsForLd = (
	activeProduct?.value?.materialsAttribute.find(
		(a) => a.__typename === "ProductMultiselectValue",
	)?.multiselect || []
)
	.map((ms) => ms.title)
	.filter((v) => !!v)
	.join(",");

const estimatedMinShippingPrice =
	activeProduct?.value?.estimatedShippingPriceRange.min?.amount;
const estimatedMaxShippingPrice =
	activeProduct?.value?.estimatedShippingPriceRange.max?.amount;
const hasEstimatedShippingRates =
	!!estimatedMinShippingPrice || !!estimatedMaxShippingPrice;

/** IMPORTANT: These also affect the product pricing (etc) info shown on google ads! Google fetches these from product pages in near real time. Google *also* uses the data from our google-connector generated products feed, but that data is less real-time, so google favors these over that. */
useJsonld(() => {
	if (!activeProduct.value) {
		return null;
	}

	// What price we're offering (selling) this product for. Confusingly named as a plural, this is just the sales price and other stuff basically.
	const baseProductOffers: Thing = {
		"@type": "Offer",
		price: currentCanonicalPrice.value,
		priceCurrency: activeProduct.value.pricing.price?.currencyCode,
		availability: getJsonldAvailability(activeProduct.value),
		priceValidUntil: promotionInfo.value.activeTo
			? formatISO(promotionInfo.value.activeTo)
			: undefined,
		url: seoUrl.value,
	};

	const baseProductSchema: JsonLD = {
		"@context": "https://schema.org",
		"@type": "Product",
		name: activeProduct.value.name ?? parentProduct.value?.name,
		// We currently don't have a full description for all products, we should in the future. In the meantime, prefer to set the `description` field and to keep `disambiguatingDescription` empty if only one description is available.
		description: fullDescription.value || shortDescription.value,

		disambiguatingDescription:
			fullDescription.value && shortDescription.value
				? shortDescription.value
				: undefined,
		image: (
			activeProduct.value.medias ||
			parentProduct.value?.medias ||
			[]
		).map((media) => ({
			"@type": "ImageObject",
			contentUrl: media.url,
		})),
		sku: activeProduct.value.sku ?? undefined,
		brand: activeProduct.value.brand
			? {
					"@type": "Brand",
					name: activeProduct.value.brand.title ?? undefined,
					url: activeProduct.value.brand.url ?? undefined,
					logo: activeProduct.value.brand.logo ?? undefined,
				}
			: undefined,
		offers: baseProductOffers,
		...(totalRatingCount.value > 0 && {
			aggregateRating: {
				"@type": "AggregateRating",
				ratingValue: rating.value,
				reviewCount: totalRatingCount.value,
			},
		}),
		...(schemaReviews.value.length > 0 && {
			review: schemaReviews.value.map((r) => ({
				"@type": "Review",
				author: {"@type": "Person", name: r.author},
				reviewRating: {
					"@type": "Rating",
					ratingValue: r.rating,
					bestRating: 5,
					worstRating: 1,
				},
				reviewBody: r.content,
				datePublished: r.date,
				publisher: {
					"@type": "Organization",
					name: "Masku Oy",
				},
			})),
		}),
	};

	if (isServiceProduct.value) {
		return baseProductSchema;
	}

	const physicalProductSchema: JsonLD = {
		...baseProductSchema,
		offers: {
			...baseProductOffers,
			hasMerchantReturnPolicy: {
				"@type": "MerchantReturnPolicy",
				returnPolicyCategory:
					"https://schema.org/MerchantReturnFiniteReturnWindow",
				merchantReturnDays: 14,
				inStoreReturnsOffered: "https://schema.org/True",
				refundType: "https://schema.org/FullRefund",
				merchantReturnLink:
					currentLang.value === "fi"
						? "https://www.masku.com/vaihdot-ja-palautukset"
						: "https://www.masku.ee/umbervahetamine-ja-tagastamine",
				returnFees: "https://schema.org/FreeReturn",
				returnMethod: [
					"https://schema.org/ReturnByMail",
					"https://schema.org/ReturnInStore",
				],
				applicableCountry: {
					"@type": "Country",
					name: t("schema.country"),
				},
			},
			shippingDetails: {
				"@type": "OfferShippingDetails",
				shippingDestination: {
					"@type": "DefinedRegion",
					addressCountry: t("schema.country"),
				},
				deliveryTime: {
					"@type": "ShippingDeliveryTime",
					handlingTime: {
						"@type": "QuantitativeValue",
						minValue: 0,
						maxValue: 1,
						unitCode: "DAY",
					},
					transitTime: {
						"@type": "QuantitativeValue",
						minValue: minimumDeliveryEstimate.value, // Not sure if this is correct
						maxValue: maximumDeliveryEstimate.value,
						unitCode: "DAY",
					},
				},
				shippingRate: hasEstimatedShippingRates
					? {
							"@type": "MonetaryAmount",
							...(estimatedMinShippingPrice && {
								value: Number(estimatedMinShippingPrice),
							}),
							...(estimatedMaxShippingPrice && {
								maxValue: Number(estimatedMaxShippingPrice),
							}),
							currency: "EUR",
						}
					: undefined,
			},
		},
		color: colorsForLd,
		material: materialsForLd,
		...dimensionsForLd,
	};

	return physicalProductSchema;
});

// FAQ structured data
useJsonld(() => {
	if (
		!activeProduct.value?.qaItems ||
		activeProduct.value.qaItems.length === 0
	) {
		return null;
	}

	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: activeProduct.value.qaItems.map((qa) => ({
			"@type": "Question",
			name: richTextToString(qa.question),
			acceptedAnswer: {
				"@type": "Answer",
				text: richTextToString(qa.answer),
			},
		})),
	};
});

const seoDescription =
	activeProduct.value.seo?.description ||
	parentProduct.value?.seo?.description ||
	sanitizedDescription.value;

useSeoMeta({
	title: activeProduct.value.name || parentProduct.value?.name,
	description: () => seoDescription,
	ogTitle: () =>
		activeProduct.value.seo.title ||
		parentProduct.value?.seo.title ||
		activeProduct.value.name ||
		parentProduct.value?.name,
	ogDescription: () => seoDescription,
	ogImage: () => medias.value[0]?.url ?? undefined,
	twitterCard: () => "summary_large_image",
});

/**
 * Computes the canonical URL for the product page
 *
 * The canonical URL is determined based on either the parent product's or active product's SEO slug.
 * If a parent product exists, its slug is used; otherwise, the active product's slug is used.
 *
 * @computed
 * @returns {string} The canonical URL for the product
 */
const canonicalUrl = computed(() => {
	if (parentProduct.value && parentProduct.value.state !== ProductState.Draft) {
		return seo.useUrl(parentProduct.value?.seo?.slug);
	}

	return seo.useUrl(activeProduct.value.seo?.slug);
});

useHead({
	meta: [{name: "keywords", content: activeProduct.value.seo?.keywords ?? ""}],
	link: [{rel: "canonical", href: canonicalUrl}],
});
</script>

<i18n lang="json">
{
	"et": {
		"wishlist": "Soovinimekiri",
		"button": {
			"add-to-cart": "Lisa ostukorvi",
			"add-to-cart-disabled": "Valik tegemata",
			"cannot-add-to-cart": "Hetkel laost otsas",
			"out-of-stock": "Hetkel laost otsas",
			"only-from-outlet": "Saadaval vaid kohapeal",
			"quantity": "tk"
		},
		"lowest-price-in-the-last-30-days": "30 päeva madalaim hind",
		"product-recommendations": {
			"style-with": "Sarnase stiiliga tooted",
			"upsell": "Ostetud ka lisaks",
			"more-from-series": "Veel samast seeriast",
			"recently-viewed": "Viimati vaadatud",
			"alternatives": "Sarnased tooted"
		},
		"promotion-active-to": "Pakkumine kehtib kuni {date} või kuniks kaupa jätkub",
		"promotion-active-until": "Pakkumine kehtib kuni {date}",
		"schema": {
			"country": "EE"
		},
		"product-description": "Toote kirjeldus"
	},
	"fi": {
		"wishlist": "Muistilista",
		"button": {
			"add-to-cart": "Lisää ostoskoriin",
			"add-to-cart-disabled": "Tee valinnat",
			"cannot-add-to-cart": "Loppu varastosta",
			"out-of-stock": "Tuote loppunut",
			"only-from-outlet": "Vain myymälöistä",
			"quantity": "kpl"
		},
		"lowest-price-in-the-last-30-days": "Alin hinta viim. 30 pv aikana",
		"product-recommendations": {
			"style-with": "Tyyliin sopivia tuotteita",
			"upsell": "Täydennä kokonaisuus",
			"more-from-series": "Samasta tuoteperheestä",
			"recently-viewed": "Katsomasi tuotteet",
			"alternatives": "Vaihtoehtoja"
		},
		"promotion-active-to": "Hinta voimassa {date} asti tai niin kauan kuin tuotetta riittää.",
		"promotion-active-until": "Hinta voimassa vähintään {date} asti.",
		"schema": {
			"country": "FI"
		},
		"product-description": "Tuotekuvaus"
	}
}
</i18n>
